import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Download } from "@mui/icons-material";
import { apiService } from "../../api/services/apiService";
import PmgAssignmentSection from "../product-management-groups/PmgAssignmentSection";
import DocumentFileUploadDialog from "./DocumentFileUploadDialog";
import { useDocuments } from "../../hooks/useDocuments";
import type { API, EntityStatus } from "../../types";
import { EntityStatus as EntityStatusEnum, EntityType } from "../../types";
import {
  downloadApiSpec,
  getApiSpecDownloadFileName,
  readSpecContent,
} from "../../utils/apiSpecBulkUpload";
import EnhanceApiDescriptionDialog from "./EnhanceApiDescriptionDialog";

interface EditApiDialogProps {
  api: API | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: API) => void;
}

const EditApiDialog: React.FC<EditApiDialogProps> = ({
  api,
  open,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<API | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pmgAccessDenied, setPmgAccessDenied] = useState(false);
  const readOnly = pmgAccessDenied;
  const [loadError, setLoadError] = useState<string | null>(null);
  const [specError, setSpecError] = useState<string | null>(null);
  const [selectedSpecFile, setSelectedSpecFile] = useState<File | null>(null);
  const [enhanceOpen, setEnhanceOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkedDocs, setLinkedDocs] = useState<
    { uuid: string; name: string; status?: string }[]
  >([]);
  const { documents: libraryDocs, loading: loadingDocs } = useDocuments();

  useEffect(() => {
    if (!open || !api?.uuid) {
      setDraft(null);
      setLoadError(null);
      setSpecError(null);
      setSelectedSpecFile(null);
      setLinkedDocs([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    apiService
      .getApi(api.uuid)
      .then((full) => {
        if (!cancelled) {
          setDraft(full);
          setLinkedDocs(
            (full.documents ?? []).map((doc) => ({
              uuid: doc.uuid,
              name: doc.name,
              status: doc.status,
            })),
          );
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(
            err instanceof Error ? err.message : "Failed to load API details",
          );
          setDraft(api);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, api]);

  const handleSpecFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const specContent = await readSpecContent(file);
      setDraft((prev) => (prev ? { ...prev, specContent } : prev));
      setSelectedSpecFile(file);
      setSpecError(null);
    } catch (err: unknown) {
      setSpecError(err instanceof Error ? err.message : "Invalid spec file");
      setSelectedSpecFile(null);
    }
  };

  const handleSave = async () => {
    if (!draft?.uuid) return;
    setSaving(true);
    try {
      const updated = await apiService.updateApi(draft.uuid, {
        name: draft.name,
        description: draft.description ?? undefined,
        version: draft.version,
        status: draft.status as EntityStatus,
        specContent: draft.specContent ?? undefined,
        documentUuids: linkedDocs.map((doc) => doc.uuid),
      });
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update API");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadSpec = () => {
    if (!draft?.specContent) return;
    downloadApiSpec(
      draft.specContent,
      getApiSpecDownloadFileName(draft.name, draft.version),
    );
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit API</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {loadError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {!loading && draft && (
          <>
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              required
              value={draft.name}
              disabled={readOnly}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, name: e.target.value } : prev,
                )
              }
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                size="small"
                startIcon={<AutoFixHighIcon />}
                disabled={loading || readOnly}
                onClick={() => setEnhanceOpen(true)}
                sx={{ textTransform: "none" }}
              >
                Enhance with AI
              </Button>
            </Box>
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              multiline
              minRows={2}
              value={draft.description ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, description: e.target.value } : prev,
                )
              }
            />
            <TextField
              label="Version"
              fullWidth
              margin="normal"
              required
              value={draft.version}
              disabled={readOnly}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, version: e.target.value } : prev,
                )
              }
            />
            <TextField
              select
              label="Status"
              fullWidth
              margin="normal"
              value={draft.status}
              disabled={readOnly}
              onChange={(e) =>
                setDraft((prev) =>
                  prev
                    ? { ...prev, status: e.target.value as EntityStatus }
                    : prev,
                )
              }
            >
              {Object.values(EntityStatusEnum).map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Library documents
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Attach existing library files or upload a new document to this API.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled={readOnly || !draft.uuid}
                onClick={() => setUploadOpen(true)}
                sx={{ textTransform: "none", mb: 1 }}
              >
                Upload document to this API
              </Button>
              {!loadingDocs && (
                <Autocomplete
                  multiple
                  disabled={readOnly}
                  options={libraryDocs
                    .filter((doc) => !linkedDocs.some((linked) => linked.uuid === doc.uuid))
                    .map((doc) => ({ uuid: doc.uuid, name: doc.name, status: doc.status }))}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
                  value={linkedDocs}
                  onChange={(_, v) => setLinkedDocs(v)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.uuid}
                        label={option.name}
                        sx={{ bgcolor: "#90e790", fontWeight: 600 }}
                      />
                    ))
                  }
                  renderInput={(p) => (
                    <TextField {...p} label="Documents" fullWidth margin="normal" />
                  )}
                />
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                API Specification
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Replace the OpenAPI spec by uploading a JSON or YAML file.
              </Typography>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                <Button
                  variant="outlined"
                  component="label"
                  sx={{ textTransform: "none" }}
                  disabled={readOnly}
                >
                  Replace spec file
                  <input
                    type="file"
                    hidden
                    accept=".json,.yaml,.yml,application/json"
                    disabled={readOnly}
                    onChange={handleSpecFileChange}
                  />
                </Button>
                {draft.specContent && (
                  <Button
                    variant="text"
                    startIcon={<Download />}
                    onClick={handleDownloadSpec}
                    sx={{ textTransform: "none" }}
                  >
                    Download current spec
                  </Button>
                )}
              </Box>

              {selectedSpecFile && (
                <Typography variant="body2" color="text.secondary">
                  Selected: <strong>{selectedSpecFile.name}</strong>
                </Typography>
              )}

              {!draft.specContent && !selectedSpecFile && (
                <Typography variant="body2" color="text.secondary">
                  No specification content on this API yet.
                </Typography>
              )}

              {specError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {specError}
                </Alert>
              )}
              {draft?.uuid && (
                <PmgAssignmentSection
                  entityUuid={draft.uuid}
                  entityType={EntityType.API}
                  onAccessDenied={setPmgAccessDenied}
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || pmgAccessDenied || !draft?.name?.trim() || !draft?.version?.trim()}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
    <EnhanceApiDescriptionDialog
      api={draft}
      open={enhanceOpen}
      onClose={() => setEnhanceOpen(false)}
      onAccept={(description) =>
        setDraft((prev) => (prev ? { ...prev, description } : prev))
      }
    />
    <DocumentFileUploadDialog
      open={uploadOpen}
      mode="file"
      apiUuids={draft?.uuid ? [draft.uuid] : []}
      onClose={() => setUploadOpen(false)}
      onComplete={(created) => {
        setLinkedDocs((prev) => [
          ...prev,
          ...created
            .filter((doc) => !prev.some((existing) => existing.uuid === doc.uuid))
            .map((doc) => ({
              uuid: doc.uuid,
              name: doc.name,
              status: doc.status,
            })),
        ]);
      }}
    />
    </>
  );
};

export default EditApiDialog;
